import React, { useMemo, useCallback, useRef, useEffect } from 'react';
import {
  SearchFacet,
  RemoveFilterCallback,
  ApplyPendingFiltersCallback,
  ApplyPendingFiltersProps,
  FacetKeyValuePair,
} from '../../../types/search';
import StatusFilterButton from './StatusFilterButton';
import PortalAppliedFilters from './PortalAppliedFilters';

type StatusQuickFiltersProps = Readonly<{
  statusFacet: SearchFacet | null;
  appliedFilters: SearchFacet[];
  requestedFilters: Record<string, string[]>;
  applyPendingFilters: ApplyPendingFiltersCallback;
  removeFilter: RemoveFilterCallback;
  calendarDateValue?: string; // e.g., "2026-01-26,2026-02-01"
  searchTerm?: string | null;
  isFetching?: boolean;
  hasFiltersApplied: boolean;
  listingType: string;
  totalResults: number;
}>;

// Helper to get a fingerprint of non-status requested filters
// Uses requestedFilters (Record<string, string[]>) which updates immediately when filters are applied
function getNonStatusFilterFingerprint(
  requestedFilters: Record<string, string[]>,
  statusKey: string | undefined
): string {
  return Object.entries(requestedFilters)
    .filter(([key]) => key.toLowerCase() !== (statusKey?.toLowerCase() ?? 'status'))
    .map(([key, values]) => {
      const sortedValues = [...values].sort((a, b) => a.localeCompare(b)).join(',');
      return `${key}:${sortedValues}`;
    })
    .sort((a, b) => a.localeCompare(b))
    .join('|');
}

export default function StatusQuickFilters({
  statusFacet,
  appliedFilters,
  requestedFilters,
  applyPendingFilters,
  removeFilter,
  calendarDateValue,
  searchTerm,
  isFetching = false,
  hasFiltersApplied,
  listingType,
  totalResults,
}: StatusQuickFiltersProps) {
  // Store the original unfiltered baseline (highest total seen, persisted across all filter changes)
  const unfilteredBaselineRef = useRef<Record<string, number>>({});
  // Cache the statusFacet along with the context it was fetched in
  const cacheRef = useRef<{
    statusFacet: SearchFacet | null;
    filterFingerprint: string;
    calendarDate: string | undefined;
    searchTerm: string | null | undefined;
  } | null>(null);

  // Calculate the current non-status filter fingerprint from requestedFilters
  // requestedFilters updates immediately when a filter is applied, unlike appliedFilters which updates after fetch
  const currentNonStatusFilterFingerprint = useMemo(
    () => getNonStatusFilterFingerprint(requestedFilters, statusFacet?.key ?? cacheRef.current?.statusFacet?.key),
    [requestedFilters, statusFacet?.key]
  );

  // Check if a status filter is currently applied (from requestedFilters, not cached data)
  const hasStatusFilterApplied = !!requestedFilters['status']?.length;

  // Determine if we should use cached data during loading
  // Only cache when:
  // 1. We have cached data
  // 2. We are currently fetching new data
  // 3. A status filter was actually applied
  // 4. The current context matches the cached context (same filters/search/date)
  const shouldUseCache =
    cacheRef.current !== null &&
    isFetching &&
    hasStatusFilterApplied &&
    currentNonStatusFilterFingerprint === cacheRef.current.filterFingerprint &&
    calendarDateValue === cacheRef.current.calendarDate &&
    searchTerm === cacheRef.current.searchTerm;

  // Use provided facet, or fall back to cached only when appropriate
  const effectiveStatusFacet = statusFacet || (shouldUseCache ? cacheRef.current?.statusFacet : null);

  // Check if we have 0 results - use totalResults prop directly
  // This is more reliable than checking facet totals which may be empty/missing when API returns 0 results
  const hasZeroResults = totalResults === 0;

  // Update cache when we have fresh data (not fetching)
  useEffect(() => {
    if (statusFacet === null) {
      // Error state - clear cache
      cacheRef.current = null;
      unfilteredBaselineRef.current = {};
    } else if (statusFacet && !isFetching) {
      // Only update cache when we have fresh data (not currently fetching)
      // Check if context changed before updating cache
      const contextChanged =
        currentNonStatusFilterFingerprint !== cacheRef.current?.filterFingerprint ||
        calendarDateValue !== cacheRef.current?.calendarDate ||
        searchTerm !== cacheRef.current?.searchTerm;

      if (contextChanged) {
        // Context changed - reset the baseline so it rebuilds with new data
        unfilteredBaselineRef.current = {};
      }

      // Update cache with current context
      cacheRef.current = {
        statusFacet,
        filterFingerprint: currentNonStatusFilterFingerprint,
        calendarDate: calendarDateValue,
        searchTerm,
      };
    }
  }, [statusFacet, isFetching, currentNonStatusFilterFingerprint, calendarDateValue, searchTerm]);

  useEffect(() => {
    // Always track the highest total we see for each status
    // This represents the absolute unfiltered count for the current date range
    effectiveStatusFacet?.items?.forEach(item => {
      const currentMax = unfilteredBaselineRef.current[item.value] ?? 0;
      if (item.total > currentMax) {
        unfilteredBaselineRef.current[item.value] = item.total;
      }
    });
  }, [effectiveStatusFacet?.items]);

  // Enhance items with baseline totals for display
  const visibleItems = useMemo(() => {
    const getDisplayTotal = (item: SearchFacet['items'][number]): number | undefined => {
      // When a status filter is applied:
      // - Selected items show actual result count (totalResults) since API facet totals may be stale
      // - Non-selected items: if results > 0, show baseline; if results = 0, show undefined (unknown)
      // When no status filter is applied, show actual totals from the API response
      if (hasStatusFilterApplied) {
        // While fetching, keep showing baseline values to prevent flicker
        // (totalResults is stale until API responds)
        if (isFetching) {
          return unfilteredBaselineRef.current[item.value];
        }
        if (item.isSelected) {
          // Use totalResults for selected status since facet data may be stale from previousFacets fallback
          return totalResults;
        }
        // If we have results, show baseline for non-selected items
        // If no results, we don't know their count for this filter combo
        return totalResults > 0 ? unfilteredBaselineRef.current[item.value] : undefined;
      }
      return item.total;
    };

    // Deduplicate by label (API returns duplicates with different values but same label)
    // Prefer the selected item, or if none selected, the one with the highest total
    const itemsByLabel = new Map<string, SearchFacet['items'][number]>();
    (effectiveStatusFacet?.items ?? []).forEach(item => {
      const label = item.label || item.value;
      const existing = itemsByLabel.get(label);

      if (existing) {
        // Prefer the selected item, or if selection status is the same, prefer higher total
        const shouldReplace =
          (item.isSelected && !existing.isSelected) ||
          (item.isSelected === existing.isSelected && item.total > existing.total);

        if (shouldReplace) {
          itemsByLabel.set(label, item);
        }
      } else {
        itemsByLabel.set(label, item);
      }
    });

    const dedupedItems = Array.from(itemsByLabel.values());

    return (
      dedupedItems
        .filter(item => {
          const displayTotal = getDisplayTotal(item);
          // Show item if: selected (always show, even with 0), or has a known positive count
          // Don't show items with undefined count - they may be from stale data (different date range)
          return item.isSelected || (displayTotal !== undefined && displayTotal > 0);
        })
        .map(item => ({
          ...item,
          displayTotal: getDisplayTotal(item),
        })) ?? []
    );
  }, [effectiveStatusFacet?.items, hasStatusFilterApplied, totalResults, isFetching]);

  const handleApplyFilter = useCallback(
    ({ facetKey, itemValue }: FacetKeyValuePair) => {
      const currentFilters = appliedFilters.reduce((acc, filter) => {
        const selectedValues = filter.items.filter(item => item.isSelected).map(item => item.value);
        if (selectedValues.length > 0) {
          acc[filter.key] = selectedValues.length === 1 ? selectedValues[0] : selectedValues;
        }
        return acc;
      }, {} as ApplyPendingFiltersProps);

      const pendingFilterState: ApplyPendingFiltersProps = {
        ...currentFilters,
        [facetKey]: itemValue,
      };

      applyPendingFilters(pendingFilterState);
    },
    [appliedFilters, applyPendingFilters]
  );

  const handleRemoveFilter = useCallback(
    (props: FacetKeyValuePair) => {
      removeFilter(props);
    },
    [removeFilter]
  );

  const hasVisibleStatusFilters = visibleItems && visibleItems.length > 0 && effectiveStatusFacet;

  // If no status filters and no applied filters, render nothing
  if (!hasVisibleStatusFilters && !hasFiltersApplied) {
    return null;
  }

  return (
    <div className="status-quick-filters-row">
      {hasVisibleStatusFilters ? (
        <fieldset className="status-quick-filters">
          <legend className="status-quick-filters__sr-only">Filter by status</legend>
          {visibleItems.map(item => (
            <StatusFilterButton
              key={`${effectiveStatusFacet.key}-${item.value}`}
              facetKey={effectiveStatusFacet.key}
              item={item}
              onApply={handleApplyFilter}
              onRemove={handleRemoveFilter}
            />
          ))}
        </fieldset>
      ) : null}
      <PortalAppliedFilters
        appliedFilters={appliedFilters}
        hasFiltersApplied={hasFiltersApplied}
        removeFilter={removeFilter}
        listingType={listingType}
        showSeparator={!!hasVisibleStatusFilters}
        disabled={hasZeroResults && hasFiltersApplied}
      />
    </div>
  );
}
